import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, SlidersHorizontal, Shield,
  Settings,
} from 'lucide-react';
import { useAuthentication } from '../../context/AuthContext';
import VerityLayout, { VerityNavItem } from './VerityLayout';
import ComingSoon from './components/ComingSoon';
import { useNavTab } from './components/useNavTab';
import { useReviewMode } from './components/useReviewMode';
import Governance from './Admin/Governance';
import RuleConfiguration from './Admin/RuleConfiguration';
import Projects from './Projects/Projects';
import StandardsLibrary from './StandardsLibrary/StandardsLibrary';
import AwpRuleLibrary from './Awp/AwpRuleLibrary';

/** A nav item that leaves IQ Verity rather than swapping the panel. */
interface AdminNavItem extends VerityNavItem {
  to?: string;
}

/**
 * ONE SCREEN FOR THE ADMINISTRATOR'S ACTUAL JOB, which is the SMRT COP shape: set a project up,
 * assign somebody to it, watch what they do with it.
 *
 * Three entries used to split that between them and none of them held it. "Reviewers & Roles" was a
 * door to the app's Users screen, which is where reviewers are created but not where they are
 * assigned. "Dashboard" showed numbers about work whose detail was one tab away. "Projects" was the
 * list with the counts on top — already the dashboard, under the wrong name.
 *
 * So Projects becomes Dashboard, and the other two go. Assigning a reviewer moves to where a project
 * is created, which is the moment somebody knows who should do it.
 */
const NAV: AdminNavItem[] = [
  // The projects list IS the admin console — who is doing what, and how far along. The separate
  // "Admin Console" entry was an empty placeholder beside it, so the name moved to the screen that
  // had earned it rather than a second one being built to deserve the name.
  { key: 'projects', label: 'Admin Console', icon: LayoutDashboard },
  { key: 'standards', label: 'Manage Standards', icon: BookOpen },
  { key: 'rules', label: 'Rule Configuration', icon: SlidersHorizontal },
  { key: 'governance', label: 'Governance', icon: Shield },
  { key: 'settings', label: 'Settings', icon: Settings },
];

/** Admin-side IQ Verity module — same IQ Verity shell, admin navigation. */
const VerityAdmin = () => {
  // In the URL, so a hard refresh stays where you were and a tab can be linked to. See useNavTab.
  const [active, setActive] = useNavTab('projects', NAV.map((n) => n.key));
  const [mode, setMode] = useReviewMode();
  const { user } = useAuthentication();
  const navigate = useNavigate();

  const userName = useMemo(
    () => user?.name || user?.displayName || user?.email || 'Administrator',
    [user]
  );

  const activeItem = NAV.find((n) => n.key === active) ?? NAV[0];

  const go = (key: string) => {
    const item = NAV.find((n) => n.key === key);
    if (item?.to) {
      navigate(item.to);
      return;
    }
    setActive(key);
  };

  // Built screens take the panel; everything else is still the placeholder.
  const panel = () => {
    if (active === 'governance') return <Governance />;
    if (active === 'rules') return <RuleConfiguration />;
    // The same screens the user side shows, not second copies. Two implementations of one list is
    // how they end up disagreeing about what a project — or a standard — is.
    //
    // Building the library lives HERE and only here: upload, extract, the requirements workbench,
    // delete. The user side gets the same component with `readOnly`, so what a reader sees is the
    // same list rather than a separate screen that has to be kept in step with this one.
    // Keyed on the mode so switching starts the list fresh rather than flashing the other side's rows.
    if (active === 'projects') return <Projects key={mode} admin reviewType={mode} />;
    // AWP standards are rule sets, read from Word catalogues — their own library, not the engineering one.
    if (active === 'standards' && mode === 'awp') return <AwpRuleLibrary />;
    if (active === 'standards') return <StandardsLibrary key={mode} reviewType={mode} />;
    return (
      <ComingSoon
        icon={activeItem.icon}
        title={activeItem.label}
        note="The admin shell, theme, and navigation are in place. Admin screens will be built out to match the IQ Verity look in a following pass."
      />
    );
  };

  return (
    <VerityLayout
      navItems={NAV}
      active={active}
      onNavigate={go}
      activeLabel={activeItem.label}
      userName={userName}
      mode={mode}
      onModeChange={setMode}
    >
      {panel()}
    </VerityLayout>
  );
};

export default VerityAdmin;
